'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  onCalculatorClick: () => void;
  onGetQuoteClick: () => void;
}

const HeroSection = ({ onCalculatorClick, onGetQuoteClick }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="relative min-h-[600px] lg:min-h-[700px]">
        <div className="absolute inset-0">
          <AppImage
            src="/assets/images/gold_jewelry_mobile_hero.png"
            alt="Elegant gold jewelry collection on neutral background"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/80 to-secondary/85"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="text-center lg:text-left">
              <h1 className="font-headline text-4xl font-bold text-primary-foreground lg:text-5xl xl:text-6xl">
                Norges mest transparente gullkjøp
              </h1>
              <p className="mt-6 font-body text-lg text-primary-foreground/90 lg:text-xl">
                Få markedspris for ditt gull med full trygghet og garantert tilfredshet
              </p>
              <div className="mt-8 flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0 lg:justify-start">
                <button className="flex items-center justify-center space-x-2 rounded-lg bg-accent px-8 py-4 font-cta text-base font-semibold text-accent-foreground shadow-cta lg:text-lg">
                  <Icon name="CalculatorIcon" size={20} variant="outline" />
                  <span>Få gratis verdivurdering</span>
                </button>
                <button className="flex items-center justify-center space-x-2 rounded-lg border-2 border-primary-foreground bg-transparent px-8 py-4 font-cta text-base font-semibold text-primary-foreground lg:text-lg">
                  <Icon name="CurrencyDollarIcon" size={20} variant="outline" />
                  <span>Se dagens gullpris</span>
                </button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative h-[500px] w-full overflow-hidden rounded-lg shadow-2xl">
                <AppImage
                  src="https://images.unsplash.com/photo-1606760227091-3dd870d97f1d"
                  alt="Elegant gold jewelry including rings, necklaces and bracelets displayed on dark velvet surface with soft lighting"
                  className="h-full w-full object-cover" />

              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section id="hero" className="relative min-h-[600px] lg:min-h-[700px]">
      <div className="absolute inset-0">
        <AppImage
          src="/assets/images/gold_jewelry_mobile_hero.png"
          alt="Elegant gold jewelry collection on neutral background"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/80 to-secondary/85"></div>
      </div>
      <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="text-center lg:text-left">
            <h1 className="font-headline text-4xl font-bold text-primary-foreground lg:text-5xl xl:text-6xl">Vi kommer hjem til deg og kjøper gullet ditt

            </h1>
            <p className="mt-6 font-body text-lg text-primary-foreground/90 lg:text-xl">
              Trygt, diskret og enkelt – få oppgjør på stedet uten sending eller venting
            </p>
            <div className="mt-8 flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0 lg:justify-start">
              <button
                onClick={onGetQuoteClick}
                className="flex items-center justify-center space-x-2 rounded-lg bg-accent px-8 py-4 font-cta text-base font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90 lg:text-lg">

                <Icon name="CalculatorIcon" size={20} variant="outline" />
                <span>bestill henting</span>
              </button>
              <button
                onClick={onCalculatorClick}
                className="flex items-center justify-center space-x-2 rounded-lg border-2 border-primary-foreground bg-transparent px-8 py-4 font-cta text-base font-semibold text-primary-foreground transition-all duration-250 hover:bg-primary-foreground/10 lg:text-lg">

                <Icon name="CurrencyDollarIcon" size={20} variant="outline" />
                <span>Se hvordan det fungerer</span>
              </button>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative h-[500px] w-full overflow-hidden rounded-lg shadow-2xl">
              <AppImage
                src="https://images.unsplash.com/photo-1606760227091-3dd870d97f1d"
                alt="Elegant gold jewelry including rings, necklaces and bracelets displayed on dark velvet surface with soft lighting"
                className="h-full w-full object-cover" />

            </div>
            <div className="absolute -bottom-6 -right-6 rounded-lg bg-card p-6 shadow-xl">
              <div className="flex items-center space-x-3">
                <Icon name="ShieldCheckIcon" size={32} variant="solid" className="text-success" />
                <div>
                  <div className="font-cta text-sm font-semibold text-foreground">100% trygt</div>
                  <div className="font-body text-xs text-muted-foreground">Garantert tilfredshet</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;